import { ICountryGroup } from '~/src/dash/api/console-api';
import { parseNumber, parseString, transformModel } from '~/src/dash/api/resource-transforms';
import {
  addDomainAndTokenToUrl,
  createUrl,
  IHttpResponse,
  restDelete,
  restGet,
  restPatch,
  restPost,
} from '~/src/dash/api/rest';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IMenuList } from '~/src/dash/types/menuList';

export const commonRegionGroupsIDs = [701, 702, 703, 704, 705, 706];

export const transformCountryGroup = transformModel<ICountryGroup>({
  ID: parseNumber(['ID']),
  PublisherID: parseNumber(['PublisherID']),
  CreatedAt: 'CreatedAt',
  UpdatedAt: 'UpdatedAt',
  DeletedAt: 'DeletedAt',
  Name: parseString(['Name']),
  CountryIsos: parseString(['CountryIsos']),
});

export async function getCountryGroupsByPublisherID(publisherID: string): Promise<ICountryGroup[]> {
  const reqUrl = addDomainAndTokenToUrl(`/country_groups`) + `&publisher_id=${publisherID}`;
  return await restGet<any>(reqUrl, {}).then((val: IHttpResponse<any>) => {
    const countryGroups = [] as ICountryGroup[];
    if (val.parsedBody) {
      for (const countryGroup of val.parsedBody.CountryGroups) {
        const parsedCountryGroup = transformCountryGroup(countryGroup);
        countryGroups.push(parsedCountryGroup);
      }
    }
    return countryGroups;
  });
}

export const generateCountryGroupOptionsList = (
  session: ISessionState,
): Promise<ISelectOption[]> => {
  const options = [] as ISelectOption[];
  return getCountryGroupsByPublisherID(session.publisher.ID)
    .then((countryGroups) => {
      countryGroups.forEach((cg) => {
        options.push({
          id: cg.ID,
          label: cg.Name,
        });
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [];
    });
};

export async function getCountryGroupsIMenuList(
  session: ISessionState,
): Promise<Record<string, IMenuList>> {
  const reqUrl =
    addDomainAndTokenToUrl(`/country_groups`) + `&publisher_id=${session.publisher.ID}`;
  return await restGet<any>(reqUrl, {})
    .then((val: IHttpResponse<any>) => {
      const countryGroups = val.parsedBody?.CountryGroups.map(transformCountryGroup);
      return {
        country_groups: generateCountryGroupsMenuList(countryGroups),
      };
    })
    .catch((e) => {
      console.error(e);
      return {
        country_groups: {},
      };
    });
}

const generateCountryGroupsMenuList = (countryGroups: ICountryGroup[]) => {
  const menuList: IMenuList = {};

  countryGroups.forEach((countryGroup: ICountryGroup) => {
    menuList[countryGroup.ID] = {
      ID: countryGroup.ID.toString(),
      selected: false,
      title: countryGroup.Name,
    };
  });

  return menuList;
};

export async function createCountryGroup(
  countryGroup: ICountryGroup,
): Promise<[ICountryGroup?, Error?]> {
  const reqUrl = createUrl('/country_groups', countryGroup.PublisherID.toString());
  const parsedCountryGroup = transformCountryGroup(countryGroup);
  // delete ID field since CountryGroup is new
  // @ts-ignore
  delete parsedCountryGroup.ID;
  try {
    const ret = (await restPost<ICountryGroup>(reqUrl, parsedCountryGroup)).parsedBody;
    return [transformCountryGroup(ret), undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function getCountryGroupsByIDs(
  countryGroups: number[],
  publisherID: string,
): Promise<[ICountryGroup[]?, Error?]> {
  const reqUrl = addDomainAndTokenToUrl(`/publisher/${publisherID}/country_groups_by_ids`);
  try {
    const fetchedCountryGroups = (
      await restPost<{ [key: string]: any }>(reqUrl, {
        // can use IBaseState instead of [key: string]: any  ?
        CountryGroupIDs: countryGroups,
      })
    ).parsedBody;

    const transformAllCountryGroups: ICountryGroup[] = (
      (fetchedCountryGroups && fetchedCountryGroups.country_groups) ||
      []
    ).map((countryGroup: ICountryGroup) => {
      return transformCountryGroup(countryGroup);
    });
    return [transformAllCountryGroups, undefined];
  } catch (e) {
    console.error(e);
    return [undefined, e];
  }
}

export async function updateCountryGroup(countryGroup: ICountryGroup): Promise<ICountryGroup> {
  const reqUrl = createUrl('/country_groups', countryGroup.PublisherID.toString());
  const parsedCountryGroup = transformCountryGroup(countryGroup);
  return await restPatch<ICountryGroup>(reqUrl, parsedCountryGroup).then(
    (val: IHttpResponse<ICountryGroup>) => {
      return transformCountryGroup(val.parsedBody);
    },
  );
}

export async function deleteCountryGroup(
  countryGroup: ICountryGroup,
): Promise<IHttpResponse<ICountryGroup>> {
  const reqUrl = createUrl('/country_groups', countryGroup.PublisherID.toString());
  const parsedCountryGroup = transformCountryGroup(countryGroup);
  return await restDelete<ICountryGroup>(reqUrl, parsedCountryGroup);
}
